import React from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { AppHeroView } from 'components/utils/AppHeroView';
import { AppSectionView } from 'components/utils/AppSectionView';
import AppCardView from 'components/utils/AppCardView';
import AppText from 'components/widgets/AppText';
import BuildIcon from '@mui/icons-material/Build';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandymanIcon from '@mui/icons-material/Handyman';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpeedIcon from '@mui/icons-material/Speed';

const MaintenancePage = () => {
    const maintenanceStats = [
        {
            icon: <BuildIcon fontSize="large" />,
            title: 'Preventive Maintenance',
            value: '98%',
            description: 'Compliance rate with maintenance schedules',
        },
        {
            icon: <HandymanIcon fontSize="large" />,
            title: 'Expert Technicians',
            value: '50+',
            description: 'Certified maintenance professionals',
        },
        {
            icon: <TimerIcon fontSize="large" />,
            title: 'Quick Turnaround',
            value: '24hrs',
            description: 'Average maintenance completion time',
        },
        {
            icon: <SafetyCheckIcon fontSize="large" />,
            title: 'Safety Checks',
            value: '100%',
            description: 'Pre-journey inspection rate',
        }
    ];

    const maintenanceServices = [
        {
            title: 'Preventive Maintenance Program',
            icon: <ScheduleIcon fontSize="large" />,
            description: 'Regular scheduled maintenance to prevent breakdowns and ensure optimal performance. Includes comprehensive vehicle inspections, fluid checks, and system diagnostics.',
            features: [
                'Multi-point inspection checklist',
                '24/7 emergency support',
                'Digital maintenance records',
                'Real-time status updates'
            ]
        },
        {
            title: 'Performance Optimization',
            icon: <SpeedIcon fontSize="large" />,
            description: 'Advanced diagnostics and tuning to maximize vehicle efficiency and performance. Our expert technicians ensure every vehicle operates at peak efficiency.',
            features: [
                'Engine performance analysis',
                'Fuel efficiency optimization',
                'Emissions control',
                'Advanced diagnostic tools'
            ]
        },
        {
            title: 'Safety Compliance',
            icon: <VerifiedIcon fontSize="large" />,
            description: 'Comprehensive safety inspections and certifications to ensure compliance with all regulatory requirements and industry standards.',
            features: [
                'Safety certification',
                'Regulatory compliance checks',
                'Safety equipment testing',
                'Documentation management'
            ]
        }
    ];

    const safetyCommitment = [
        'Manufacturer-trained technicians',
        'State-of-the-art diagnostic equipment',
        'Digital maintenance tracking system',
        '24/7 emergency support network'
    ];

    return (
        <Box>
            <AppHeroView
                title="Vehicle Maintenance Excellence"
                description="Ensuring peak performance and safety through comprehensive maintenance programs"
            />

            {/* Stats Overview */}
            <AppSectionView
                title="Maintenance Excellence"
                description="Our commitment to maintaining a reliable and safe fleet is reflected in our comprehensive maintenance program and expert team of technicians."
                items={maintenanceStats}
                renderItem={(stat) => (
                    <AppCardView
                        icon={stat.icon}
                        title={stat.value}
                        description={stat.title}
                        subtitle={stat.description}
                    />
                )}
            />

            {/* Services Section */}
            <Box sx={{ bgcolor: 'grey.100', py: 6 }}>
                <Container maxWidth="lg">
                    <AppText
                        text="Our Maintenance Services"
                        variant="h4"
                        align="center"
                        gutterBottom
                    />
                    <AppText
                        text="Comprehensive maintenance solutions tailored for chassis transport excellence"
                        variant="subtitle1"
                        align="center"
                        color="text.secondary"
                        sx={{ mb: 4 }}
                    />
                    <Grid container spacing={4}>
                        {maintenanceServices.map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Box sx={{ color: 'primary.main', mb: 2 }}>
                                            {service.icon}
                                        </Box>
                                        <AppText
                                            text={service.title}
                                            variant="h6"
                                            gutterBottom
                                        />
                                        <AppText
                                            text={service.description}
                                            color="text.secondary"
                                            paragraph
                                        />
                                        <Divider sx={{ my: 2 }} />
                                        <List dense>
                                            {service.features.map((feature, idx) => (
                                                <ListItem key={idx}>
                                                    <ListItemIcon>
                                                        <CheckCircleIcon color="success" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={feature} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Safety Commitment */}
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Paper sx={{ p: 4, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    <AppText
                        text="Our Commitment to Excellence"
                        variant="h5"
                        gutterBottom
                    />
                    <AppText
                        text="At Avis Logistics, we understand that reliable chassis transport depends on meticulous maintenance and unwavering commitment to safety. Our state-of-the-art facilities and expert technicians ensure your fleet receives the highest standard of care."
                        paragraph
                    />
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        {safetyCommitment.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VerifiedIcon />
                                    <AppText text={item} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            </Container>

            {/* CTA Section */}
            <Container maxWidth="md" sx={{ textAlign: 'center', py: 6 }}>
                <AppText
                    text="Need Maintenance Support?"
                    variant="h4"
                    gutterBottom
                />
                <AppText
                    text="Our 24/7 maintenance support team is ready to assist you. Contact us for immediate assistance or to schedule routine maintenance."
                    color="text.secondary"
                    paragraph
                />
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Box textAlign="center">
                        <LocalShippingIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                        <AppText text="24/7 Support" variant="h6" />
                        <AppText text="+91-9113XXXXXX" color="text.secondary" />
                    </Box>
                    <Box textAlign="center">
                        <EngineeringIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                        <AppText text="Service Centers" variant="h6" />
                        <AppText text="Pan India Network" color="text.secondary" />
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default MaintenancePage;